import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { categoryList, categoryMeta } from '@/mocks/categoryData';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState<'popular' | 'latest' | 'rating'>('popular');

  const category = categoryList.find((c) => c.slug === slug);
  const meta = slug ? categoryMeta[slug] : null;

  useEffect(() => {
    if (meta) {
      document.title = meta.title;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute('content', meta.description);
      const keywords = document.querySelector('meta[name="keywords"]');
      if (keywords) keywords.setAttribute('content', meta.keywords);
    }
  }, [meta]);

  useEffect(() => {
    if (!category) return;

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://nh5688.com/' },
        { '@type': 'ListItem', position: 2, name: category.name, item: 'http://nh5688.com/category/all' },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(breadcrumbScript);
    };
  }, [category, slug]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <h1 className="text-2xl font-heading text-foreground-900 mb-2">分类未找到</h1>
          <p className="text-foreground-500 mb-6">该分类不存在或已被移除</p>
          <Link to="/" className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const sortedList = [...category.manhuaList].sort((a, b) => {
    if (activeSort === 'rating') return parseFloat(b.rating) - parseFloat(a.rating);
    if (activeSort === 'latest') return parseInt(b.latestChapter.replace(/[^0-9]/g, '')) - parseInt(a.latestChapter.replace(/[^0-9]/g, ''));
    return parseInt(b.views.replace(/[^0-9]/g, '')) - parseInt(a.views.replace(/[^0-9]/g, ''));
  });

  return (
    <div className="min-h-screen pt-16">
      <div className="px-4 md:px-6 py-8 md:py-12">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category.name },
            ]}
          />

          <div className="mt-4 mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100">
                <i className={`${category.icon} text-xl text-primary-600`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                {category.name}
              </h1>
            </div>
            <p className="text-foreground-600 text-sm max-w-xl">{category.description}</p>
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-foreground-500 text-sm">
              共 <strong className="text-foreground-700">{category.manhuaList.length}</strong> 部作品
            </p>
            <div className="flex items-center bg-background-100 rounded-full p-1">
              {[
                { key: 'popular', label: '人气' },
                { key: 'latest', label: '最新' },
                { key: 'rating', label: '评分' },
              ].map((sort) => (
                <button
                  key={sort.key}
                  onClick={() => setActiveSort(sort.key as typeof activeSort)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeSort === sort.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {sort.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {sortedList.map((item) => (
              <Link
                key={item.id}
                to={`/manhua/${item.id}`}
                className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    title={`${item.title} - 虚空韩漫网`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-0.5 rounded-md bg-primary-500/90 text-background-50 text-xs font-medium">
                      {item.rating}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                    <p className="text-background-200 text-xs">{item.latestChapter}</p>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-foreground-900 text-sm font-medium truncate group-hover:text-primary-500 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-foreground-500 text-xs mt-1 truncate">{item.author}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}